import React, { useState, useCallback, useEffect } from 'react';
import { Page, Layout, LegacyCard, Collapsible, TextContainer, Icon, Card, Text } from '@shopify/polaris';
import { ChevronDownIcon, ChevronUpIcon } from '@shopify/polaris-icons';
import { FetchCustom } from './FetchCustom';
import { useHostNavigate } from "../hooks";

export function Faq(props) {
    const navigate = useHostNavigate();

    const [model, setModel] = useState({ automaticDiscounts: false, enableApp: false, specificCustomers: false, syncDiscounts: false, cancelSubscription: false, leaveCode: false });
    const [redirectUrl, setRedirectUrl] = useState('');

    const fetch = FetchCustom();

    const getUser = async () => {
        if (!props.userLoaded) {
            props.setUserLoaded(true);
            const data = await fetch('api/home/userInfo');

            if (data.reauthorizeUrl) {
                return;
            }

            if (data.redirectUrl) {
                setRedirectUrl(data.redirectUrl);
            }
            else if (JSON.stringify(props.user) !== JSON.stringify(data)) {
                props.setUser(data);
                localStorage.setItem('user', JSON.stringify(data));
            }
        }
    };

    const handleExpand = useCallback(
        (id) => {
            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model[id] = !model[id];
                return model;
            });
        },
        [],
    );

    useEffect(() => {
        props.setIsLoading(true);
        document.title = "FAQ | Stack Discounts";
        props.setIsLoading(false);

        getUser();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (redirectUrl) {
            navigate(redirectUrl);
        }
    }, [redirectUrl, navigate]);

    return (
        <div className="faq-page">
            <Page title="FAQ">
                <Layout>
                    <Layout.Section>
                        <Card>
                            <div className="faq-header" onClick={() => { handleExpand('automaticDiscounts') }}>
                                <Text as="h2" variant="headingMd">How to handle automatic discounts?</Text>
                                {
                                    model.automaticDiscounts ? <Icon source={ChevronUpIcon} /> : <Icon source={ChevronDownIcon} />
                                }
                            </div>
                            <Collapsible
                                open={model.automaticDiscounts}
                                id="automaticDiscounts"
                                transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}
                                expandOnPrint
                            >
                                <TextContainer>
                                    <p>The app does not support automatic discounts; you cannot assign automatic discounts to the campaign.</p>
                                    <p>Because the app applies all discounts assigned to campaigns automatically, you can create a regular discount with the same parameters as the automatic one and use that in campaigns. It will work in a similar way.</p>
                                </TextContainer>
                            </Collapsible>
                        </Card>
                    </Layout.Section>
                    <Layout.Section>
                        <Card>
                            <div className="faq-header" onClick={() => { handleExpand('enableApp') }}>
                                <Text as="h2" variant="headingMd">Still cannot see the app on the storefront?</Text>
                                {
                                    model.enableApp ? <Icon source={ChevronUpIcon} /> : <Icon source={ChevronDownIcon} />
                                }
                            </div>
                            <Collapsible
                                open={model.enableApp}
                                id="enableApp"
                                transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}
                                expandOnPrint
                            >
                                <TextContainer>
                                    <p>1. Make sure the app is enabled on the app's home page.</p>
                                    <p>2. Make sure Live mode is enabled on the app's home page.</p>
                                    <div>3. Make sure the app is enabled in your theme settings
                                        <ul>
                                            <li>Navigate to the theme settings page in the Shopify admin of your store.</li>
                                            <li>Enable Stack Discounts in the "App embeds" section and save changes.</li>
                                        </ul>
                                    </div>
                                </TextContainer>
                            </Collapsible>
                        </Card>
                    </Layout.Section>
                    <Layout.Section>
                        <Card>
                            <div className="faq-header" onClick={() => { handleExpand('specificCustomers') }}>
                                <Text as="h2" variant="headingMd">Does the app support discounts for specific customers?</Text>
                                {
                                    model.specificCustomers ? <Icon source={ChevronUpIcon} /> : <Icon source={ChevronDownIcon} />
                                }
                            </div>
                            <Collapsible
                                open={model.specificCustomers}
                                id="specificCustomers"
                                transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}
                                expandOnPrint
                            >
                                <TextContainer>
                                    <p>If you have a discount with "Customer eligibility -&#x3e; Specific customers" or "Customer eligibility -&#x3e; Specific groups of customers" specified, this discount will work in the app, only if the customer is logged in.</p>
                                    <p>If the customer is not logged in we cannot identify him before the checkout step.</p>
                                </TextContainer>
                            </Collapsible>
                        </Card>
                    </Layout.Section>
                    <Layout.Section>
                        <Card>
                            <div className="faq-header" onClick={() => { handleExpand('syncDiscounts') }}>
                                <Text as="h2" variant="headingMd">Why aren't changes made to a discount in Shopify being reflected in Stack Discounts | PG?</Text>
                                {
                                    model.syncDiscounts ? <Icon source={ChevronUpIcon} /> : <Icon source={ChevronDownIcon} />
                                }
                            </div>
                            <Collapsible
                                open={model.syncDiscounts}
                                id="syncDiscounts"
                                transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}
                                expandOnPrint
                            >
                                <TextContainer>
                                    <p>Changes made to a discount in Shopify admin will be automatically reflected in the app within the 5-minute intervals.</p>
                                    <p>If you want to see your changes immediately in the app you can click on the "Sync discounts" button on the "Campaigns" page of the app.</p>
                                </TextContainer>
                            </Collapsible>
                        </Card>
                    </Layout.Section>
                    <Layout.Section>
                        <Card>
                            <div className="faq-header" onClick={() => { handleExpand('cancelSubscription') }}>
                                <Text as="h2" variant="headingMd">Can I cancel my subscription?</Text>
                                {
                                    model.cancelSubscription ? <Icon source={ChevronUpIcon} /> : <Icon source={ChevronDownIcon} />
                                }
                            </div>
                            <Collapsible
                                open={model.cancelSubscription}
                                id="cancelSubscription"
                                transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}
                                expandOnPrint
                            >
                                <TextContainer>
                                    <p>To cancel your subscription all you need to do is uninstall Stack Discounts | PG.</p>
                                </TextContainer>
                            </Collapsible>
                        </Card>
                    </Layout.Section>
                    <Layout.Section>
                        <Card>
                            <div className="faq-header" onClick={() => { handleExpand('leaveCode') }}>
                                <Text as="h2" variant="headingMd">Will Stack Discounts | PG leave any code once uninstalled?</Text>
                                {
                                    model.leaveCode ? <Icon source={ChevronUpIcon} /> : <Icon source={ChevronDownIcon} />
                                }
                            </div>
                            <Collapsible
                                open={model.leaveCode}
                                id="leaveCode"
                                transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}
                                expandOnPrint
                            >
                                <TextContainer>
                                    <p>The app will not leave any code once removed/uninstalled.</p>
                                    <p>The app doesn't alter your theme in any way either.</p>
                                </TextContainer>
                            </Collapsible>
                        </Card>
                    </Layout.Section>
                </Layout>
            </Page>
        </div>
    );
}